/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { request } from '@/api/client';
import { activityBus } from '@/lib/activity';

/**
 * Sign-in, and who the caller is.
 *
 * There is no token here and there never will be. The browser holds an httpOnly
 * cookie it cannot read, and `credentials: 'include'` sends it (AUTH-5).
 */

export const meSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  display_name: z.string(),
  initials: z.string(),
  roles: z.array(z.string()),
  team_count: z.number(),
  /** How long a session survives idle, and how much notice precedes its end. */
  session_idle_timeout_minutes: z.number(),
  session_warning_seconds: z.number(),
});

export type Me = z.infer<typeof meSchema>;

export const permissionsSchema = z.object({
  permissions: z.array(z.string()),
});

export const sessionSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  display_name: z.string(),
  roles: z.array(z.string()),
  expires_at: z.string(),
});

export const meQuery = () =>
  queryOptions({
    queryKey: ['me'] as const,
    queryFn: ({ signal }) => request('/api/v1/me', meSchema, { signal }),
    retry: false,
    staleTime: 60_000,
  });

export const permissionsQuery = () =>
  queryOptions({
    queryKey: ['permissions'] as const,
    queryFn: ({ signal }) =>
      request('/api/v1/me/permissions', permissionsSchema, { signal }).then(
        (r) => new Set(r.permissions),
      ),
    retry: false,
    staleTime: 60_000,
  });

export const authModeSchema = z.object({
  mode: z.enum(['local', 'oidc']),
  provider: z.string(),
});

export type AuthMode = z.infer<typeof authModeSchema>;

/**
 * How this deployment signs people in.
 *
 * Asked before anybody is authenticated, so it is the one query on the
 * sign-in screen — and it answers with nothing but the method.
 */
export const authModeQuery = () =>
  queryOptions({
    queryKey: ['auth-mode'] as const,
    queryFn: ({ signal }) => request('/api/v1/auth/mode', authModeSchema, { signal }),
    retry: false,
    staleTime: Infinity,
  });

export const localPersonSchema = z.object({
  email: z.string(),
  display_name: z.string(),
  description: z.string(),
  roles: z.array(z.string()),
});

export type LocalPerson = z.infer<typeof localPersonSchema>;

/**
 * Who the local chooser offers.
 *
 * Read from the server rather than listed here. The server owns who exists and
 * what each of them can do; a copy in the frontend would be a second answer to
 * the same question, and the two would drift.
 */
export const localPeopleQuery = () =>
  queryOptions({
    queryKey: ['local-people'] as const,
    queryFn: ({ signal }) =>
      request('/api/v1/auth/local-people', z.array(localPersonSchema), { signal }),
    retry: false,
    staleTime: Infinity,
  });

export async function signIn(email: string) {
  return request('/api/v1/auth/signin', sessionSchema, {
    method: 'POST',
    body: { email },
  });
}

export const heartbeatSchema = z.object({
  status: z.literal('ok'),
  idle_timeout_minutes: z.number(),
});

/**
 * Extend the idle window.
 *
 * Called when a person says they are still there, never on a timer — a
 * background poll that kept a session alive would make the timeout a fiction.
 */
export const heartbeat = () =>
  request('/api/v1/auth/heartbeat', heartbeatSchema, { method: 'POST' });

export const signOutSchema = z.object({
  /** The identity provider's own sign-out page, or null where there is none. */
  logout_url: z.string().nullable(),
});

/** Send the whole browser somewhere else: the provider's sign-out page. */
const leaveTheApp = (url: string) => {
  window.location.assign(url);
};

/**
 * End the session, here and in every other tab — and, by default, at the
 * identity provider too (AUTHN-8).
 *
 * Vellum's own session ends first, on the server. Then, if the server names a
 * provider sign-out page, the browser goes there: without it, the provider's
 * single sign-on would let the next person at a shared machine press Sign in
 * and be let straight back in as this one (audit CMP-16). The provider sends
 * the browser back to the sign-in screen afterwards.
 *
 * @param options.provider false to end only Vellum's session — the idle
 *   timeout, which must not sign somebody out of every Microsoft app on the
 *   machine because a Vellum tab sat untouched.
 * @param options.go how to leave; a test passes its own.
 * @returns true when the browser is on its way to the provider, so the caller
 *   need not route anywhere itself.
 * @throws ApiError when the server did not confirm it. The cache is cleared
 *   either way, so a caller can say the sign-out failed without leaving the
 *   last person's data behind it.
 */
export async function signOut(
  client: QueryClient,
  {
    provider = true,
    go = leaveTheApp,
  }: { provider?: boolean; go?: (url: string) => void } = {},
): Promise<boolean> {
  let logoutUrl: string | null;
  try {
    ({ logout_url: logoutUrl } = await request('/api/v1/auth/signout', signOutSchema, {
      method: 'POST',
    }));
    // Only once the server has ended it: a tab told to go to the sign-in
    // screen for a session that is still alive would be telling a lie.
    activityBus.announceSignedOut();
  } finally {
    // Clear every cached answer, whether or not the server answered: the next
    // person to sign in must not see the previous one's data sitting in the
    // cache, and a failed request is no reason to keep it.
    client.clear();
  }
  if (!provider || logoutUrl === null) return false;
  go(logoutUrl);
  return true;
}
